import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, X, Pencil, Archive, ArchiveRestore, Wallet, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../api/client';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';
const PROFESSION_LABELS = { apotheker: 'Apotheker', dokter: 'Dokter', therapeut: 'Therapeut', kinesist: 'Kinesist', verpleegkundige: 'Verpleegkundige', anders: 'Anders' };

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <span className="text-muted-foreground w-36 shrink-0 font-medium text-sm">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

export default function AdminB2BDetail() {
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [commissionRate, setCommissionRate] = useState('15');
  const [discountRate, setDiscountRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getB2B(id).then(r => {
      setPerson(r.data);
      setCommissionRate(String(r.data.commissionRate ?? 15));
      setDiscountRate(r.data.discountRate != null ? String(r.data.discountRate) : '');
    }).finally(() => setLoading(false));
  }, [id]);

  const approve = async () => {
    setSaving(true);
    await adminApi.approveB2B(id);
    setPerson(p => ({ ...p, isApproved: true, approvedAt: new Date().toISOString() }));
    setSaving(false);
  };

  const reject = async () => {
    if (!window.confirm('B2B-aanvraag intrekken?')) return;
    setSaving(true);
    await adminApi.rejectB2B(id);
    setPerson(p => ({ ...p, isApproved: false }));
    setSaving(false);
  };

  const archive = async () => {
    if (!window.confirm('Deze voorschrijver archiveren?')) return;
    setSaving(true);
    try {
      await adminApi.archivePrescriber(id);
      setPerson(p => ({ ...p, archived: true, archivedAt: new Date().toISOString() }));
      toast.success('Gearchiveerd');
    } finally { setSaving(false); }
  };

  const restore = async () => {
    setSaving(true);
    try {
      await adminApi.restorePrescriber(id);
      setPerson(p => ({ ...p, archived: false, archivedAt: null }));
      toast.success('Hersteld');
    } finally { setSaving(false); }
  };

  const saveRates = async () => {
    setSaving(true);
    await adminApi.updateB2BRates(id, {
      commissionRate: parseFloat(commissionRate) || 15,
      discountRate: discountRate === '' ? null : parseFloat(discountRate),
    });
    setPerson(p => ({
      ...p,
      commissionRate: parseFloat(commissionRate) || 15,
      discountRate: discountRate === '' ? null : parseFloat(discountRate),
    }));
    setSaving(false);
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;
  if (!person) return <div className="p-8 text-muted-foreground">B2BPerson niet gevonden.</div>;

  const org = person.org;
  const b2bUser = person.user;

  return (
    <div data-testid="admin-b2b-detail" className="p-8 max-w-2xl">
      <Link to="/admin/b2b" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> B2B Aanvragen
      </Link>

      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl font-light text-foreground flex items-center gap-2 flex-wrap">
            {[person.firstName, person.lastName].filter(Boolean).join(' ') || b2bUser?.name || '--'}
            {person.archived && (
              <span data-testid="detail-archived-pill" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-muted text-muted-foreground border border-border">ARCHIVED</span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{person.email || b2bUser?.email || '--'}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Bron: <span className="font-medium">{person.source || 'admin'}</span>
            {person.userId
              ? <> · <span className="text-emerald-700 font-semibold">LINKED</span></>
              : <> · <span className="text-slate-600 font-semibold">UNLINKED</span></>}
          </p>
        </div>
        <span
          data-testid="b2b-status-badge"
          className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${person.isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
        >
          {person.isApproved ? 'Goedgekeurd' : 'In afwachting'}
        </span>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-8">
        <Link
          to={`/admin/b2b/${id}/edit`}
          data-testid="edit-prescriber-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" /> Bewerken
        </Link>
        <Link
          to={`/admin/credits?personId=${id}`}
          data-testid="open-credits-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors"
        >
          <Wallet className="w-3.5 h-3.5" /> Credits openen
        </Link>
        {person.archived ? (
          <button
            data-testid="restore-prescriber-btn" onClick={restore} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors disabled:opacity-50"
          >
            <ArchiveRestore className="w-3.5 h-3.5" /> Herstellen
          </button>
        ) : (
          <button
            data-testid="archive-prescriber-btn" onClick={archive} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-card border border-border text-muted-foreground hover:border-destructive/30 hover:text-destructive transition-colors disabled:opacity-50"
          >
            <Archive className="w-3.5 h-3.5" /> Archiveren
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 space-y-3 shadow-sm">
        <Row label="Bedrijf">{org?.companyName || '--'}</Row>
        <Row label="BTW-nummer">{org?.vatNumber || '--'}</Row>
        <Row label="Adres">
          {[org?.addressStreet, org?.addressNumber, org?.addressBus].filter(Boolean).join(' ')}
          {org?.addressCity ? `, ${org.addressPostalCode} ${org.addressCity}` : ''}
          {org?.addressCountry ? `, ${org.addressCountry}` : ''}
          {!org?.addressStreet && '--'}
        </Row>
        <Row label="Beroep">
          {PROFESSION_LABELS[person.profession] || person.profession}
          {person.professionOther ? ` -- ${person.professionOther}` : ''}
        </Row>
        <Row label="Telefoon">{person.phone || '--'}</Row>
        {person.approvedAt && (
          <Row label="Goedgekeurd op">{new Date(person.approvedAt).toLocaleDateString('nl-BE', { dateStyle: 'medium' })}</Row>
        )}
      </div>

      {/* Rates */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 shadow-sm">
        <h2 className="font-serif text-base font-medium mb-4 text-foreground">Commissie & korting</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">Commissierate (%)</label>
            <input
              data-testid="commission-rate"
              type="number" step="0.1" value={commissionRate}
              onChange={e => setCommissionRate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5 text-foreground">Individuele korting (%)</label>
            <input
              data-testid="discount-rate"
              type="number" step="0.1" min="0" max="100" placeholder="bv. 10"
              value={discountRate} onChange={e => setDiscountRate(e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-muted-foreground mt-1">Laat leeg voor geen individuele korting.</p>
          </div>
        </div>
        <button
          data-testid="save-rates-btn"
          onClick={saveRates} disabled={saving}
          className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95"
        >
          Tarieven opslaan
        </button>
      </div>

      {/* VAT Reversed */}
      {org && (
        <div className="bg-card border border-border rounded-2xl p-6 mb-6 shadow-sm">
          <h2 className="font-serif text-base font-medium mb-4 text-foreground">BTW-verlegging (Art. 39bis)</h2>
          <div className="space-y-2 text-sm mb-4">
            <Row label="Status">
              <span className={org.vatReversed ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                {org.vatReversed ? 'Verlegd — BTW = 0' : 'Normaal — BTW van toepassing'}
              </span>
            </Row>
            {org.vatReversedOverriddenAt && (
              <Row label="Gewijzigd op">{new Date(org.vatReversedOverriddenAt).toLocaleDateString('nl-BE', { dateStyle: 'medium' })}</Row>
            )}
          </div>
          <button
            data-testid="toggle-vat-reversed"
            onClick={async () => {
              setSaving(true);
              const newVal = !org.vatReversed;
              await adminApi.updateB2BVatReversed(id, newVal);
              setPerson(p => ({ ...p, org: { ...p.org, vatReversed: newVal, vatReversedOverriddenAt: new Date().toISOString() } }));
              setSaving(false);
            }}
            disabled={saving}
            className="px-6 py-2.5 border border-border rounded-full text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
          >
            {org.vatReversed ? 'BTW-verlegging deactiveren' : 'BTW-verlegging activeren'}
          </button>
        </div>
      )}

      {/* Linked user (conservative: exact-email suggestions only) */}
      <LinkUserSection person={person} onLinked={(userId, user) => setPerson(p => ({ ...p, userId, user }))} />

      {/* Approve / Reject */}
      <div className="flex gap-3">
        {!person.isApproved && (
          <button
            data-testid="approve-b2b-btn"
            onClick={approve} disabled={saving}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95 shadow-sm"
          >
            <Check className="w-4 h-4" /> Goedkeuren
          </button>
        )}
        {person.isApproved && (
          <button
            data-testid="reject-b2b-btn"
            onClick={reject} disabled={saving}
            className="inline-flex items-center gap-2 bg-destructive text-destructive-foreground rounded-full px-8 py-3 text-sm font-semibold hover:bg-destructive/90 disabled:opacity-50 transition-all active:scale-95"
          >
            <X className="w-4 h-4" /> Intrekken
          </button>
        )}
      </div>
    </div>
  );
}

function LinkUserSection({ person, onLinked }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const id = person._id;
  const hasEmail = !!person.email;

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getPrescriberLinkSuggestions(id);
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const link = async (user) => {
    if (!window.confirm(`Koppel gebruiker ${user.email} aan deze voorschrijver?`)) return;
    setLinking(true);
    try {
      await adminApi.linkPrescriberUser(id, user._id);
      onLinked(user._id, user);
      toast.success('Gebruiker gekoppeld');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Koppelen mislukt');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div data-testid="link-user-section" className="bg-card border border-border rounded-2xl p-6 mb-6 shadow-sm">
      <h2 className="font-serif text-base font-medium mb-4 text-foreground flex items-center gap-2">
        <Link2 className="w-4 h-4" /> Gekoppelde gebruiker
      </h2>
      {person.user ? (
        <div data-testid="linked-user" className="text-sm text-foreground">
          <div className="font-medium">{person.user.name || person.user.email}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{person.user.email} · userId {person.user._id}</div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Deze voorschrijver heeft nog geen gekoppeld gebruikersaccount.
            {hasEmail
              ? ' Zoek voorstellen op basis van exact e-mailadres — koppeling gebeurt nooit automatisch.'
              : ' Voeg eerst een e-mailadres toe voordat je voorstellen kunt opzoeken.'}
          </p>
          <button
            data-testid="find-link-suggestions-btn"
            onClick={loadSuggestions}
            disabled={!hasEmail || loading}
            className="px-5 py-2 rounded-full text-xs font-semibold bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors disabled:opacity-50"
          >
            {loading ? 'Zoeken…' : 'Voorstellen zoeken'}
          </button>
          {suggestions != null && suggestions.length === 0 && (
            <p className="text-xs text-muted-foreground italic">Geen exacte e-mailmatch gevonden.</p>
          )}
          {suggestions && suggestions.length > 0 && (
            <ul data-testid="link-suggestions" className="divide-y divide-border border border-border rounded-lg overflow-hidden">
              {suggestions.map(u => (
                <li key={u._id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium">{u.name || u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <button
                    data-testid={`link-user-${u._id}`}
                    onClick={() => link(u)} disabled={linking}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-all"
                  >
                    Koppelen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
